'use client'

import { useRouter } from 'next/navigation'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { buildCreditsLimitConfig } from '@/components/upgrade/upgrade-limit-config'
import { useOrgContext } from '@/contexts/org-context'
// biome-ignore lint/suspicious/noImportCycles: pre-existing import cycle (madge-era baseline) — do not extend; unwind when touching this module
import { useOptionalUpgradeLimit } from '@/contexts/upgrade-limit-context'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'
import { useOptionalCan } from '@/hooks/use-can'
import { isConvexOrgReady, useConvexOrgId } from '@/hooks/use-convex-org-id'
import { USD_PER_CREDIT } from '@/lib/billing/credit-economics'
import { isBillingPlanKey } from '@/lib/billing/plans'
import type { BillingOverview } from '@/lib/billing/types'
import { appToast } from '@/lib/shared/toast'

interface UsageLimitsValue {
  isLoading: boolean
  planKey: string
  planName: string
  planLimits: {
    credits: number
    domains: number
    emailSends: number
    brands: number
    hasWatermark: boolean
  }
  usage: {
    credits: number
    emailSends: number
  }
  usagePercent: number
  /**
   * Credits and sends actually GRANTED for the open billing period. These are
   * what "how much do I have this period" must read — `planLimits` is the
   * plan's monthly catalog capability, and a prepaid multi-month term grants
   * several months at once, so the two legitimately differ.
   */
  includedCredits: number
  includedEmailSends: number
  remainingCredits: number
  remainingEmailSends: number
  rolloverCredits: number
  /** USD value of one credit. Mirrored so consumers don't import the constant. */
  usdPerCredit: number
  /** USD value of credits already spent this period. */
  creditsUsedUsd: number
  /** USD value of credits still available this period. */
  remainingCreditsUsd: number
  /** Total USD spend recorded against the open billing period. */
  periodCostUsd: number
  status: string
  isGrowthCommitment: boolean
  currentPeriodEnd: number | null
  portalEligible: boolean
  /**
   * True when a plan change (a downgrade) is scheduled for the next renewal —
   * i.e. the Stripe price already sits below the open-period entitlement. While
   * this holds, an in-app upgrade is rejected by `changeOrgSubscriptionPlan`,
   * so surfaces must route to /plan (resolve via "Keep") instead of charging.
   */
  hasScheduledPlanChange: boolean
  /**
   * Whether this org has ever had paid billing (including churned customers
   * now back on `free`). Gates the never-paid-only Growth promo.
   */
  hasEverPaid: boolean
  showLimitDialog: (limitType: 'domains' | 'credits') => void
}

const UsageLimitsContext = createContext<UsageLimitsValue | null>(null)

function toUsageLimitsValue(
  snapshot: BillingOverview | undefined,
  showLimitDialog: UsageLimitsValue['showLimitDialog']
): UsageLimitsValue {
  if (!snapshot) {
    return {
      isLoading: true,
      planKey: 'free',
      planName: 'Free',
      planLimits: {
        credits: 500,
        domains: 1,
        emailSends: 1000,
        brands: 1,
        hasWatermark: true,
      },
      usage: {
        credits: 0,
        emailSends: 0,
      },
      usagePercent: 0,
      includedCredits: 500,
      includedEmailSends: 1000,
      remainingCredits: 500,
      remainingEmailSends: 1000,
      rolloverCredits: 0,
      usdPerCredit: USD_PER_CREDIT,
      creditsUsedUsd: 0,
      remainingCreditsUsd: 500 * USD_PER_CREDIT,
      periodCostUsd: 0,
      status: 'free',
      isGrowthCommitment: false,
      currentPeriodEnd: null,
      portalEligible: false,
      hasScheduledPlanChange: false,
      // Loading placeholder: assume paid so a promo surface can never flash
      // for an org that turns out to be ineligible. Promotion is server-led.
      hasEverPaid: true,
      showLimitDialog,
    }
  }

  return {
    isLoading: false,
    planKey: snapshot.planKey,
    planName: snapshot.planName,
    planLimits: snapshot.planLimits,
    usage: snapshot.usage,
    usagePercent: snapshot.creditUsagePercent,
    includedCredits: snapshot.includedCredits,
    includedEmailSends: snapshot.includedEmailSends,
    remainingCredits: snapshot.remainingCredits,
    remainingEmailSends: snapshot.remainingEmailSends,
    rolloverCredits: snapshot.rolloverCredits,
    usdPerCredit: snapshot.usdPerCredit,
    creditsUsedUsd: snapshot.creditsUsedUsd,
    remainingCreditsUsd: snapshot.remainingCreditsUsd,
    periodCostUsd: snapshot.periodCostUsd,
    status: snapshot.status,
    isGrowthCommitment: snapshot.isGrowthCommitment,
    currentPeriodEnd: snapshot.currentPeriodEnd,
    portalEligible: snapshot.portalEligible,
    hasScheduledPlanChange: snapshot.hasScheduledPlanChange,
    hasEverPaid: snapshot.hasEverPaid,
    showLimitDialog,
  }
}

export function UsageLimitsProvider({
  initialSnapshotPromise,
  children,
}: {
  /**
   * Server-kicked usage-snapshot promise, streamed across the RSC boundary
   * (never rejects — resolves `null` when the server fetch failed). Adopted
   * via effect below — deliberately NOT `use()`: this provider wraps the
   * entire authed app, so suspending on the promise would put the usage read
   * back on the shell's critical paint path (the exact block this removes).
   * The live, reactive value is subscribed via `useAuthedQuery` and takes
   * over once the Convex auth handshake lands.
   */
  initialSnapshotPromise: Promise<BillingOverview | null>
  children: React.ReactNode
}) {
  // Seed adopted whenever the streamed promise lands; consumers render the
  // `isLoading` defaults until then (same as the failed-fetch `null` path).
  const [initialSnapshot, setInitialSnapshot] =
    useState<BillingOverview | null>(null)
  useEffect(() => {
    let isCancelled = false
    void initialSnapshotPromise.then((snapshot) => {
      if (!isCancelled && snapshot) {
        setInitialSnapshot(snapshot)
      }
    })
    return () => {
      isCancelled = true
    }
  }, [initialSnapshotPromise])
  const router = useRouter()
  const { orgId } = useOrgContext()
  // The org id carried by the live Convex JWT. During a Clerk org switch the
  // server-seeded `orgId` updates before the JWT settles; firing
  // `getUsageSnapshot` with the not-yet-authorized id throws (and billing
  // throws redact to a fatal "Server Error" that `OrgAuthSwitchGate` rethrows).
  // Hold the query until the JWT claim matches — mirrors the brand-list gate in
  // `contexts/brand-context.tsx`.
  const convexOrgId = useConvexOrgId()
  // Live subscription for reactive credit/limit updates, for EVERY org role:
  // usage is member-readable (only billing identity and control stay
  // admin-only). `useAuthedQuery` skips until the Clerk→Convex handshake
  // completes (so it never throws pre-handshake); the `isConvexOrgReady` gate
  // additionally holds it through the org-switch window. We render
  // `initialSnapshot` until the live value arrives. No `serverAccessToken` is
  // ever sent from the client.
  const liveSnapshot = useAuthedQuery(
    api.billing.queries.getUsageSnapshot,
    isConvexOrgReady(convexOrgId, orgId) ? { orgId } : 'skip'
  )
  const snapshot = liveSnapshot ?? initialSnapshot ?? undefined
  const upgrade = useOptionalUpgradeLimit()
  // The upgrade dialog starts a checkout / plan change, which is org-admin
  // work; it never opens for anyone else.
  const canManageBilling = useOptionalCan('org.billing')

  const showLimitDialog = useCallback(
    (limitType: 'domains' | 'credits') => {
      if (
        limitType === 'credits' &&
        canManageBilling === true &&
        upgrade &&
        snapshot &&
        isBillingPlanKey(snapshot.planKey) &&
        snapshot.planKey !== 'enterprise'
      ) {
        const limit = snapshot.includedCredits + (snapshot.rolloverCredits ?? 0)
        upgrade.showUpgrade(
          buildCreditsLimitConfig({
            currentPlanKey: snapshot.planKey,
            used: limit,
            limit,
          })
        )
        return
      }

      appToast.warning(
        limitType === 'domains' ? 'Domain limit reached' : 'Out of AI credits'
      )
      // /plan is member-readable (usage only for non-admins), so everyone
      // lands on the page that shows where the limit stands.
      router.push('/plan')
    },
    [canManageBilling, router, snapshot, upgrade]
  )

  const value = useMemo(
    () => toUsageLimitsValue(snapshot, showLimitDialog),
    [showLimitDialog, snapshot]
  )

  return (
    <UsageLimitsContext.Provider value={value}>
      {children}
    </UsageLimitsContext.Provider>
  )
}

export function useUsageLimitsOptional() {
  return useContext(UsageLimitsContext)
}
